import { usePublicReveals } from './usePublicReveals';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { ArrowUpRight, Command, Menu, Search, X } from 'lucide-react';
import { useAuth } from '../auth';

const searchItems = [
  { label: 'Storage overview', detail: 'Files, uploads, and CDN', path: '/#storage', external: false },
  { label: 'Log Observatory', detail: 'Ingest and search JSON logs', path: '/#logs', external: false },
  { label: 'Pricing', detail: 'Monthly and annual plans', path: '/pricing', external: false },
  { label: 'REST API documentation', detail: 'Authentication and examples', path: '/docs', external: false },
  { label: 'SDKs', detail: 'FiveM, RedM, Node.js and Python SDKs, downloads and examples', path: '/sdks', external: false },
  { label: 'FiveM & RedM SDK installation', detail: 'Install the Lua resource and connect your key', path: '/sdks/fivem-redm', external: false },
  { label: 'Node.js SDK installation', detail: 'Install @bckt/sdk with npm and make your first upload', path: '/sdks/nodejs', external: false },
  { label: 'Python SDK installation', detail: 'Install bckt-sdk with pip and use sync or async clients', path: '/sdks/python', external: false },
  { label: 'S3 gateway setup', detail: 'Connect an S3-compatible client', path: '/docs#s3', external: false },
  { label: 'System status', detail: 'Better Stack status and uptime monitoring', path: 'https://status.bckt.io', external: true },
];

function SearchPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const filtered = searchItems.filter((item) =>
    `${item.label} ${item.detail}`.toLowerCase().includes(query.toLowerCase()),
  );

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement;
    setQuery('');
    setActive(0);
    document.body.dataset.locked = 'true';
    requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      delete document.body.dataset.locked;
      previous?.focus();
    };
  }, [open]);

  if (!open) return null;
  const select = (item: (typeof searchItems)[number]) => {
    onClose();
    if (item.external) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.path);
    }
  };
  return (
    <div className="palette" role="dialog" aria-modal="true" aria-label="Search BCKT">
      <button className="palette__scrim" aria-label="Close search" onClick={onClose} />
      <div
        className="palette__panel"
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((value) => Math.min(value + 1, filtered.length - 1));
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((value) => Math.max(value - 1, 0));
          }
          if (event.key === 'Enter' && filtered[active]) select(filtered[active]);
          if (event.key === 'Tab') {
            const nodes = event.currentTarget.querySelectorAll<HTMLElement>('input,button');
            if (!nodes.length) return;
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }
        }}
      >
        <div className="palette__input">
          <Search size={17} />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            placeholder="Search BCKT"
            aria-label="Search"
          />
          <kbd>Esc</kbd>
        </div>
        <div className="palette__results">
          {filtered.length ? (
            filtered.map((item, index) => (
              <button
                key={item.path}
                className={index === active ? 'palette__item is-active' : 'palette__item'}
                onMouseEnter={() => setActive(index)}
                onClick={() => select(item)}
              >
                <span>
                  {item.label}
                  <small>{item.detail}</small>
                </span>
                <ArrowUpRight size={15} />
              </button>
            ))
          ) : (
            <p className="palette__empty">No result for “{query}”.</p>
          )}
        </div>
        <div className="palette__foot">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> move
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
        </div>
      </div>
    </div>
  );
}

export function Brand() {
  return (
    <Link className="brand" to="/" aria-label="BCKT home">
      <span className="brand__mark" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      <span>BCKT</span>
    </Link>
  );
}

export function PublicHeader() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen((value) => !value);
      }
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  return (
    <>
      <header className="site-header">
        <div className="site-header__inner">
          <Brand />
          <nav
            className={mobileOpen ? 'site-nav is-open' : 'site-nav'}
            aria-label="Main navigation"
          >
            <NavLink to="/#product" onClick={() => setMobileOpen(false)}>Product</NavLink>
            <NavLink to="/pricing" onClick={() => setMobileOpen(false)}>Pricing</NavLink>
            <NavLink to="/docs" onClick={() => setMobileOpen(false)}>Docs</NavLink>
            <NavLink to="/sdks" onClick={() => setMobileOpen(false)}>SDKs</NavLink>
          </nav>
          <button
            className="search-pill"
            onClick={() => setSearchOpen(true)}
            aria-label="Search BCKT"
          >
            <Search size={15} />
            <span>Search</span>
            <span className="search-pill__keys">
              <Command size={11} />K
            </span>
          </button>
          <div className="site-header__actions">
            <Link className="text-link" to={user ? '/dashboard' : '/login'}>
              {user ? 'Dashboard' : 'Sign in'}
            </Link>
            <Link
              className="button button--light button--small"
              to={user ? '/dashboard' : '/signup'}
            >
              {user ? 'Open BCKT' : 'Start free'}
            </Link>
          </div>
          <button
            className="menu-button"
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((value) => !value)}
          >
            {mobileOpen ? <X /> : <Menu />}
          </button>
        </div>
      </header>
      <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}

export function PublicFooter() {
  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__meta">
          <Brand />
          <nav aria-label="Footer">
            <Link to="/pricing">Pricing</Link>
            <Link to="/docs">Documentation</Link>
            <Link to="/sdks">SDKs</Link>
            <a href="https://status.bckt.io" target="_blank" rel="noopener noreferrer">Status</a>
            <Link to="/report">Report abuse</Link>
            <Link to="/fair-use">Fair use</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/acceptable-use">Acceptable use</Link>
            <Link to="/branding">Branding</Link>
            <a href="mailto:hello@bckt.io">Contact</a>
          </nav>
          <span>© 2026 BCKT</span>
        </div>
      </div>
    </footer>
  );
}

export function PublicLayout({ children }: { children: ReactNode }) {
  const content = useRef<HTMLElement>(null);
  const location = useLocation();
  usePublicReveals(content, location.pathname);
  return (
    <>
      <PublicHeader />
      <main ref={content}>{children}</main>
      <PublicFooter />
    </>
  );
}
